<template>
  <div>
    <div class="flex flex-wrap" v-if="isAdmin">
      <div class="w-full mb-12 xl:mb-0 px-4">
        <card-bar-chart
          :records="horasPorFecha"
          label="fecha"
          value="horas"
          title="Horas por fecha"
          datasetName="Horas"
        />
      </div>
    </div>
    <div class="flex flex-wrap mt-4" v-if="isAdmin">
      <div class="w-full mb-12 xl:mb-0 px-4">
        <card-list
          title="Resumen de Proyectos"
          primaryKey="idpresupuesto"
          resource="proyectos"
          :records="horasPorPresupuesto"
          :meta="horasPorPresupuestoMeta"
          :columns="horasPorPresupuestoColumns"
          readonly="true"
        >
          <template v-slot:after-table>
            <card-list
              title="Totales"
              :records="totalHorasPorPresupuestoRecords"
              :columns="totalHorasPorPresupuestoColumns"
              readonly="true"
              hideFilters="true"
              doNotSort="true"
              unableSelect="true"
              hideHeaders="true"
              hideActions="true"
            />
          </template>
        </card-list>
      </div>
      <div class="w-full xl:w-4/12 px-4"></div>
    </div>
    <div class="flex flex-wrap mt-4" v-if="!isAdmin">
      <div class="w-full mb-0 px-4">
        <card-scheduler subtitle="Mis horas" />
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import ReporteService from "@/services/reporte.service";
import CardBarChart from "@/components/Cards/CardBarChart.vue";
import CardList from "@/components/Cards/CardList.vue";
import CardScheduler from "@/components/Cards/CardScheduler.vue";

export default {
  name: "dashboard-page",

  components: {
    CardBarChart,
    CardList,
    CardScheduler,
  },

  data() {
    return {
      horasPorFecha: [],
      horasPorFechaColumns: [
        {
          label: "Fecha",
          name: "fecha",
          type: "DATE",
        },
        {
          label: "Horas",
          name: "horas",
          type: "DECIMAL",
        },
      ],
      totalHorasPorPresupuestoColumns: [
        {
          name: "label",
          type: "STRING",
        },
        {
          name: "value",
          type: "DECIMAL",
          currency: true,
        },
      ],
      totalHorasPorPresupuesto: {},
      totalCostoEngapa: {},
      totalMontoComparativo: {},
      totalCostoInterno: {},
      totalCostoExterno: {},
      horasPorPresupuesto: [],
      horasPorPresupuestoMeta: {},
      horasPorPresupuestoColumns: [
        {
          label: "Presupuesto",
          name: "nombre",
          type: "STRING",
          // Se muestra el nombre, pero se filtra eligiendo el proyecto
          filterAs: { name: "idpresupuesto", type: "presupuesto" },
        },
        {
          label: "Horas",
          name: "horas",
          type: "DECIMAL",
          group: "Filtro",
          color: "#10b98142",
        },
        {
          label: "Costo interno",
          name: "costo_interno",
          type: "DECIMAL",
          currency: true,
          group: "Filtro",
        },
        {
          label: "Costo externo",
          name: "costo_externo",
          type: "DECIMAL",
          currency: true,
          group: "Filtro",
        },
        {
          label: "%",
          name: "porcentaje",
          type: "DECIMAL",
          doNotSort: true,
          group: "Filtro",
        },
        {
          label: "Costo del proyecto",
          name: "costo_engapa",
          type: "DECIMAL",
          currency: true,
          group: "Histórico",
        },
        {
          label: "Cobrado",
          name: "cobrado",
          type: "DECIMAL",
          currency: true,
          group: "Histórico",
        },
        {
          label: "Monto comparativo (USD)",
          name: "monto_total_comparativo",
          type: "DECIMAL",
          currency: true,
          group: "Histórico",
        },
        {
          label: "Fecha envío",
          name: "fecha_enviado",
          type: "DATE",
        },
        {
          label: "Fecha inicio",
          name: "fecha_inicio",
          type: "DATE",
        },
        {
          label: "Fecha fin",
          name: "fecha_fin",
          type: "DATE",
        },
      ],
    };
  },

  mounted: function () {
    this.loadHorasPorFecha();
  },

  watch: {
    $route: {
      immediate: true,
      handler() {
        this.loadHorasPorPresupuesto();
        const properties = ["startDate", "endDate"];

        properties.forEach((property) => {
          this.$watch(
            () => this[property],
            () => {
              this.loadHorasPorFecha();
              this.loadHorasPorPresupuesto();
            }
          );
        });
      },
    },
  },

  methods: {
    loadHorasPorFecha() {
      if (this.isAdmin) {
        const startDate = this.startDate;
        const endDate = this.endDate;
        // Se le pasa el mismo filtro del Resumen de Proyectos, para que el
        // gráfico acompañe a la tabla y no muestre proyectos que ésta oculta
        const filters = {
          startDate,
          endDate,
          filters: this.$route.query.filters,
        };

        ReporteService.horasPorFecha(filters)
          .then((data) => {
            this.horasPorFecha = data.records;
          })
          .catch((response) => {
            console.log(response);
          });
      }
    },

    loadHorasPorPresupuesto() {
      if (this.isAdmin) {
        const params = { ...this.$route.query };
        params.startDate = this.startDate;
        params.endDate = this.endDate;
        params.filter = this.$route.query.filter;

        ReporteService.horasPorPresupuesto(params)
          .then((data) => {
            const totalHoras = data.meta.totalHoras;
            const records = data.records.map((obj) => ({
              ...obj,
              porcentaje: ((parseFloat(obj.horas) / totalHoras) * 100).toFixed(
                2
              ),
            }));
            this.horasPorPresupuesto = records;
            this.horasPorPresupuestoMeta = data.meta;
            this.totalHorasPorPresupuesto = totalHoras;
            this.totalCostoEngapa = data.meta.totalCostoEngapa;
            this.totalMontoComparativo = data.meta.totalMontoComparativo;
            this.totalCostoInterno = data.meta.totalCostoInterno;
            this.totalCostoExterno = data.meta.totalCostoExterno;
          })
          .catch((response) => {
            console.log(response);
          });
      }
    },
  },

  computed: {
    ...mapState({
      userInfo: (state) => state.auth.user,
      startDate: (state) => state.filters.startDate,
      endDate: (state) => state.filters.endDate,
    }),
    currentRoutePath() {
      return this.$route.path;
    },
    isAdmin() {
      return this.userInfo?.roles?.includes("admin");
    },
    totalHorasPorPresupuestoRecords() {
      return [
        { label: "Horas", value: this.totalHorasPorPresupuesto },
        { label: "Costo interno", value: this.totalCostoInterno },
        { label: "Costo externo", value: this.totalCostoExterno },
        { label: "Costo del proyecto", value: this.totalCostoEngapa },
        { label: "Monto comparativo", value: this.totalMontoComparativo },
      ];
    },
  },
};
</script>
